import { useMemo, useState } from "react";
import { Alert, Box, Button, CircularProgress, MenuItem, Paper, Stack, TextField, Typography, useTheme } from "@mui/material";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import TableViewOutlinedIcon from "@mui/icons-material/TableViewOutlined";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import ConnString from "../../../../ConnString.js";
import ReportLoadingOverlay from "../ReportLoadingOverlay.jsx";

const Connstring2 = ConnString.connstring;
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const numberFormatter = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const escapeHtml = (value) => String(value ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
const formatAsOf = ({ month, year }) => new Date(year, month, 0).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });

const BalanceSheet = () => {
  const theme = useTheme();
  const dark = theme.palette.mode === "dark";
  const today = new Date();
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [year, setYear] = useState(today.getFullYear());
  const [parameters, setParameters] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const compID = sessionStorage.getItem("compID");
  const companyName = sessionStorage.getItem("compName") || sessionStorage.getItem("selectedCompany") || "INFORMATION SYSTEMS AUDIT AND CONTROL ASSOCIATION (MANILA CHAPTER), INC.";
  const displayCompanyName = companyName.replace(/\s*\(ISACA\)\s*$/i, "");

  const years = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 12 }, (_, index) => currentYear + 1 - index);
  }, []);
  const card = { bgcolor: dark ? "#182033" : "#fff", border: `1px solid ${dark ? "rgba(255,255,255,.08)" : "#e5eaf1"}`, borderRadius: "16px", boxShadow: dark ? "none" : "0 8px 28px rgba(15,23,42,.05)" };
  const muted = dark ? "#94a3b8" : "#64748b";

  const handleGenerate = async (event) => {
    event.preventDefault();
    setError("");
    if (!compID) {
      setError("Company information is missing. Please sign in again.");
      return;
    }
    const lastDay = new Date(year, month, 0).getDate();
    const nextParameters = { month, year, dateFrom: `${month}/1/${year}`, dateTo: `${month}/${lastDay}/${year}` };
    setLoading(true);
    try {
      const response = await fetch(`${Connstring2}/api/Reports/ISACA/BalanceSheet`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ compID, ...nextParameters }),
      });
      const data = await response.json();
      if (!response.ok || data.success === false) throw new Error(data.message || "Unable to generate the Balance Sheet.");
      setRows(Array.isArray(data.rows) ? data.rows : []);
      setParameters(nextParameters);
    } catch (err) {
      setRows([]);
      setParameters(null);
      setError(err.message || "Unable to generate the Balance Sheet.");
    } finally {
      setLoading(false);
    }
  };

  const fileName = () => `ISACA-Balance-Sheet-${parameters.year}-${String(parameters.month).padStart(2, "0")}`;

  const printReport = () => {
    if (!parameters || !rows.length) return;
    const printWindow = window.open("", "_blank", "width=900,height=900");
    if (!printWindow) {
      setError("Please allow pop-ups to print the Balance Sheet.");
      return;
    }
    const reportRows = rows.map((row) => {
      const rowType = row.RowType || "detail";
      if (["section", "group"].includes(rowType)) return `<tr class="${rowType}"><td colspan="2">${escapeHtml(row.Description)}</td></tr>`;
      return `<tr class="${rowType}"><td>${escapeHtml(row.Description)}</td><td>${escapeHtml(numberFormatter.format(Number(row.Amount || 0)))}</td></tr>`;
    }).join("");
    printWindow.document.write(`<!doctype html><html><head><title>Balance Sheet - ${MONTHS[parameters.month - 1]} ${parameters.year}</title><style>@page{size:A4 portrait;margin:12mm}*{box-sizing:border-box}body{font-family:Arial,sans-serif;color:#111;margin:0;font-size:10px}h1{font-size:22px;margin:0 0 2px}.company,.period{font-weight:700}.company{font-size:10px}.period{font-size:9px;margin-bottom:14px}table{width:520px;border-collapse:collapse}.month th{text-align:right;border-bottom:1px solid #111;padding:3px}.month th:first-child{text-align:left}.section td{font-weight:800;text-decoration:underline;border-bottom:1px solid #111;padding:10px 0 3px}.group td{font-weight:800;padding:10px 0 2px 10px}.detail td{padding:1px 3px}.detail td:first-child{padding-left:24px}.detail td:last-child,.total td:last-child,.major-total td:last-child,.grand-total td:last-child{text-align:right}.total td,.major-total td,.grand-total td{font-weight:800;padding:2px 3px;border-top:1px solid #bbb}.major-total td,.grand-total td{padding-top:7px;border-bottom:1px solid #777}.grand-total td{border-top:1px solid #111;border-bottom:1px solid #111}</style></head><body><h1>BALANCE SHEET</h1><div class="company">${escapeHtml(displayCompanyName.toUpperCase())}</div><div class="period">As of ${escapeHtml(formatAsOf(parameters))}</div><table><thead><tr class="month"><th></th><th>${escapeHtml(MONTHS[parameters.month - 1])} ${parameters.year}</th></tr></thead><tbody>${reportRows}</tbody></table><script>window.onload=()=>{window.print();window.onafterprint=()=>window.close();};</script></body></html>`);
    printWindow.document.close();
  };

  const downloadExcel = () => {
    if (!parameters || !rows.length) return;
    const sheetRows = [["BALANCE SHEET"], [displayCompanyName.toUpperCase()], [`As of ${formatAsOf(parameters)}`], [], ["", `${MONTHS[parameters.month - 1]} ${parameters.year}`], ...rows.map((row) => [row.Description, row.Amount === null ? "" : Number(row.Amount || 0)])];
    const worksheet = XLSX.utils.aoa_to_sheet(sheetRows);
    worksheet["!cols"] = [{ wch: 48 }, { wch: 18 }];
    worksheet["!merges"] = [0, 1, 2].map((r) => ({ s: { r, c: 0 }, e: { r, c: 1 } }));
    rows.forEach((row, index) => {
      if (row.Amount === null) return;
      const cell = worksheet[XLSX.utils.encode_cell({ r: index + 5, c: 1 })];
      if (cell) cell.z = "#,##0.00;[Red]-#,##0.00";
    });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Balance Sheet");
    XLSX.writeFile(workbook, `${fileName()}.xlsx`);
  };

  const downloadPdf = () => {
    if (!parameters || !rows.length) return;
    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const left = 16;
    const amountX = 136;
    let y = 17;
    pdf.setTextColor(17, 24, 39);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(20);
    pdf.text("BALANCE SHEET", left, y);
    y += 5;
    pdf.setFontSize(8.7);
    pdf.text(displayCompanyName.toUpperCase(), left, y);
    y += 4;
    pdf.setFontSize(8);
    pdf.text(`As of ${formatAsOf(parameters)}`, left, y);
    y += 9;
    pdf.setFontSize(9);
    pdf.text(`${MONTHS[parameters.month - 1]} ${parameters.year}`, amountX, y, { align: "right" });
    pdf.line(left + 6, y + 1.5, amountX, y + 1.5);
    y += 5;
    const addPage = () => {
      pdf.addPage(); y = 16; pdf.setFont("helvetica", "bold"); pdf.setFontSize(8.5); pdf.text(`${MONTHS[parameters.month - 1]} ${parameters.year}`, amountX, y, { align: "right" }); y += 5;
    };
    rows.forEach((row) => {
      if (y > 280) addPage();
      const rowType = row.RowType || "detail";
      if (rowType === "section") {
        y += 4; pdf.setFont("helvetica", "bold"); pdf.setFontSize(9.5); pdf.text(row.Description, left + 6, y); pdf.line(left + 6, y + 1, amountX, y + 1); y += 5; return;
      }
      if (rowType === "group") {
        y += 2.5; pdf.setFont("helvetica", "bold"); pdf.setFontSize(8.5); pdf.text(row.Description, left + 10, y); y += 4; return;
      }
      const isTotal = rowType.includes("total");
      pdf.setFont("helvetica", isTotal ? "bold" : "normal"); pdf.setFontSize(8.2); pdf.text(row.Description, left + (isTotal ? 6 : 14), y); pdf.text(numberFormatter.format(Number(row.Amount || 0)), amountX, y, { align: "right" });
      if (isTotal) pdf.line(left + 6, y + 1, amountX, y + 1);
      y += rowType === "grand-total" ? 6 : 4.2;
    });
    pdf.save(`${fileName()}.pdf`);
  };

  return (
    <Box sx={{ minHeight: "100%", p: { xs: 2, md: 3 }, bgcolor: dark ? "#111827" : "#f7f8fc" }}>
      <ReportLoadingOverlay
        open={loading}
        reportName="Balance Sheet"
        period={`As of ${new Date(year, month, 0).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" })}`}
      />
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={2} mb={3}>
        <Stack direction="row" spacing={1.5} alignItems="center"><Box sx={{ width: 46, height: 46, display: "grid", placeItems: "center", borderRadius: "13px", bgcolor: dark ? "#6366f122" : "#eef2ff", color: "#5b5ce2" }}><AccountBalanceOutlinedIcon /></Box><Box><Typography fontSize={{ xs: 24, md: 29 }} fontWeight={800}>Balance Sheet</Typography><Typography color={muted} fontSize={13}>ISACA Financial Report</Typography></Box></Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button disabled={!rows.length || loading} onClick={downloadExcel} variant="outlined" startIcon={<TableViewOutlinedIcon />} sx={{ textTransform: "none", borderRadius: "10px" }}>Excel</Button>
          <Button disabled={!rows.length || loading} onClick={downloadPdf} variant="outlined" startIcon={<PictureAsPdfOutlinedIcon />} sx={{ textTransform: "none", borderRadius: "10px" }}>PDF</Button>
          <Button disabled={!rows.length || loading} onClick={printReport} variant="outlined" startIcon={<PrintOutlinedIcon />} sx={{ textTransform: "none", borderRadius: "10px" }}>Print</Button>
        </Stack>
      </Stack>
      <Paper component="form" onSubmit={handleGenerate} elevation={0} sx={{ ...card, p: { xs: 2, md: 2.5 }, mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" mb={2.5}><CalendarMonthOutlinedIcon sx={{ color: "#6366f1", fontSize: 21 }} /><Typography fontSize={16} fontWeight={800}>Report parameters</Typography></Stack>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "minmax(200px,1fr) minmax(150px,.8fr) auto" }, gap: 2, alignItems: "end", maxWidth: 720 }}>
          <TextField select label="Month" value={month} onChange={(event) => setMonth(Number(event.target.value))} size="small">{MONTHS.map((name, index) => <MenuItem key={name} value={index + 1}>{name}</MenuItem>)}</TextField>
          <TextField select label="Year" value={year} onChange={(event) => setYear(Number(event.target.value))} size="small">{years.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}</TextField>
          <Button type="submit" disabled={loading} variant="contained" startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <AccountBalanceOutlinedIcon />} sx={{ minHeight: 40, px: 2.5, bgcolor: "#5b5ce2", textTransform: "none", borderRadius: "10px", boxShadow: "none", whiteSpace: "nowrap" }}>{loading ? "Generating…" : "Generate report"}</Button>
        </Box>
      </Paper>
      {error && <Alert severity="warning" sx={{ mb: 2, borderRadius: "12px" }}>{error}</Alert>}
      <Paper elevation={0} sx={{ ...card, minHeight: 360, p: { xs: 2, md: 4 }, overflowX: "auto" }}>
        {parameters && rows.length ? (
          <Box sx={{ width: "100%", maxWidth: 720, mx: "auto", color: dark ? "#f8fafc" : "#111" }}>
            <Typography fontSize={25} fontWeight={900} lineHeight={1.1}>BALANCE SHEET</Typography>
            <Typography fontSize={11.5} fontWeight={800} mt={0.5}>{displayCompanyName.toUpperCase()}</Typography>
            <Typography fontSize={11} fontWeight={700} mb={2.2}>As of {formatAsOf(parameters)}</Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "minmax(320px,1fr) 145px", px: 1, pb: 0.5, borderBottom: `1px solid ${dark ? "#94a3b8" : "#111"}` }}><Box /><Typography textAlign="right" fontSize={12} fontWeight={800}>{MONTHS[parameters.month - 1]} {parameters.year}</Typography></Box>
            {rows.map((row, index) => {
              const rowType = row.RowType || "detail";
              if (rowType === "section") return <Typography key={index} fontSize={14} fontWeight={900} mt={2.2} px={1} pb={0.3} sx={{ textDecoration: "underline", borderBottom: `1px solid ${dark ? "#64748b" : "#777"}` }}>{row.Description}</Typography>;
              if (rowType === "group") return <Typography key={index} fontSize={12.5} fontWeight={900} mt={1.8} ml={2} mb={0.25}>{row.Description}</Typography>;
              const isTotal = rowType.includes("total");
              return <Box key={index} sx={{ display: "grid", gridTemplateColumns: "minmax(320px,1fr) 145px", alignItems: "end", px: 1, py: rowType === "grand-total" ? 0.75 : 0.25, ml: isTotal ? 0 : 3.5, mt: ["major-total", "grand-total"].includes(rowType) ? 1 : 0, fontWeight: isTotal ? 900 : 400 }}><Typography fontSize={12.2} fontWeight="inherit">{row.Description}</Typography><Typography textAlign="right" fontSize={12.2} fontWeight="inherit" sx={{ borderBottom: isTotal ? `1px solid ${dark ? "#94a3b8" : "#111"}` : "none", borderTop: rowType === "grand-total" ? `1px solid ${dark ? "#94a3b8" : "#111"}` : "none" }}>{numberFormatter.format(Number(row.Amount || 0))}</Typography></Box>;
            })}
          </Box>
        ) : <Box sx={{ minHeight: 290, display: "grid", placeItems: "center", textAlign: "center" }}><Box><AccountBalanceOutlinedIcon sx={{ fontSize: 48, color: dark ? "#475569" : "#cbd5e1", mb: 1 }} /><Typography fontWeight={800}>Select report parameters</Typography><Typography color={muted} fontSize={13}>Choose the month and year, then generate the Balance Sheet.</Typography></Box></Box>}
      </Paper>
    </Box>
  );
};

export default BalanceSheet;
